/**
 * Supabase Album Service
 *
 * Supabase-backed implementation of {@link IAlbumService} — the only album
 * data implementation, exposed via
 * {@link module:features/albums/services/albumService}.
 *
 * Mapping notes (app camelCase ⇄ DB snake_case):
 *  - Photo.url ⇄ photos.url, uploadedBy ⇄ uploaded_by,
 *    uploadedAt ⇄ uploaded_at, isCover ⇄ is_cover, takenAt ⇄ taken_at,
 *    taggedMembers ⇄ tagged_members
 *  - Album.coverPhotoId ⇄ albums.cover_photo_id, memberIds ⇄ member_ids,
 *    createdBy ⇄ created_by
 *
 * Known schema gaps (documented for QA — no column to persist into):
 *  - Album.photoCount: derived by counting `photos` rows per album.
 *  - Album.coverUrl: derived from the cover photo's key, signed at read time.
 *  - Album.emoji: no `albums.emoji` column — DERIVED from the persisted
 *    category in {@link rowToAlbum}, which is where the icon used to be lost.
 *  - Album.dateRange: no column — never persisted/returned.
 *
 * SIGNING, AND WHY THE MAPPERS TAKE A LOOKUP
 *   Since migration 020 the `photos` bucket is private and `photos.url` holds a
 *   RAW OBJECT KEY. Nothing renderable is stored, so every read path has to
 *   mint a signed URL before a component sees the row.
 *
 *   The obvious shape — make `rowToPhoto` async and `await createSignedUrl`
 *   inside it — is the shape `supabaseCalendarService.rowToAttachment` already
 *   has, and it is fine there because an event carries three attachments. An
 *   album carries two hundred, and one HTTP POST per object means the grid
 *   waits on 400 sequential round-trips before it paints. So the mappers stay
 *   SYNCHRONOUS and pure, and each list method signs its whole page in ONE
 *   request via `createSignedUrls` and hands the resulting `key -> URL` lookup
 *   down. A mapper called without a lookup still produces a valid Photo, with
 *   `storagePath` intact and `url` empty, which the render sites show as a
 *   placeholder rather than as a broken image.
 *
 * OFFLINE WRITES, AND WHY THE GATE HAS TWO SIDES
 *   `navigator.onLine === false` is the cheap check and runs first, before
 *   anything touches the wire — and nothing a queued entry does not need is
 *   resolved above it, which is why the update and delete paths resolve their
 *   family scope only after the gate.
 *
 *   The expensive case is a browser reporting `onLine === true` over a captive
 *   portal or a dead uplink. postgrest-js does not reject there; it answers
 *   with an error envelope. So each write also classifies its own failure with
 *   {@link isTransientWriteFailure} and enqueues on the transport class only —
 *   never on a refusal, which would poison the replay queue for every table.
 *
 * @module features/albums/services/supabaseAlbumService
 */

import { getSupabaseClient } from '@/shared/clients/supabaseClient';
import { getCurrentFamilyId } from '@/shared/lib/familyContext';
import {
  enqueueSupabaseMutation,
  isTransientWriteFailure,
} from '@/shared/lib/offlineMutationQueue';
import { sanitizeText } from '@/shared/lib/sanitize';
import {
  SIGNED_URL_TTL_SECONDS,
  createSignedUrl,
  createSignedUrls,
  removeFiles,
  storageKeyForRemoval,
  storageKeyFromValue,
  type ImageTransform,
} from '@/shared/services/storageService';
import type {
  Enums,
  Json,
  Tables,
  TablesInsert,
  TablesUpdate,
} from '@/shared/types/database';
import type {
  AddPhotoInput,
  Album,
  AlbumFilters,
  AlbumSortBy,
  CreateAlbumInput,
  Photo,
  PhotoComment,
  PhotoMetadata,
  PhotoVisibility,
  SortDirection,
  UpdateAlbumInput,
  UpdatePhotoInput,
} from '../types/album.types';
import {
  ALBUM_CATEGORY_CONFIG,
  DEFAULT_ALBUM_SETTINGS,
  DEFAULT_PHOTO_SETTINGS,
} from '../types/album.types';
import type { IAlbumService } from './albumService.types';

type AlbumRow = Tables<'albums'>;
type PhotoRow = Tables<'photos'>;
type PhotoCommentRow = Tables<'photo_comments'>;

/** The bucket every album photo lives in. Private since migration 020. */
const PHOTO_BUCKET = 'photos';

/**
 * Resize applied to an album cover on the SINGLE-album paths.
 *
 * A cover is rendered in a 4:3 card, so shipping the 50 MB original for it is
 * pure waste. On a private bucket a resize is a read-time parameter of the
 * signature, which is why this can exist at all without a second stored object.
 *
 * It is used only where exactly ONE cover is being signed (`getAlbum`,
 * `updateAlbum`, `setAlbumCover`). The album LIST signs every cover in one
 * batch, and the batch endpoint has no transform parameter — see the note on
 * `createSignedUrls`. The asymmetry is deliberate: one request with full-size
 * covers beats N requests with small ones.
 */
const ALBUM_COVER_TRANSFORM: ImageTransform = {
  width: 640,
  height: 480,
  resize: 'cover',
};

/** Empty lookup, so an unsigned mapper call needs no null checks downstream. */
const NO_SIGNATURES: ReadonlyMap<string, string> = new Map();

/**
 * What the writes with NO offline queue branch say when the wire is the problem.
 *
 * `setAlbumCover` and `deleteComment` were never given an `isOffline()` gate or
 * a transient fallback — Wave 4 covered the other five album writes and stopped.
 * Giving them one now would be new behaviour, so they keep the shortfall; what
 * they may NOT keep is the lie that came with it. Every other refusal in this
 * file says «нямате права», and saying that to a parent whose phone has dropped
 * off the network would be a brand-new falsehood, invented by the very change
 * that exists to stop this service lying about writes. So a transport failure on
 * those two paths says this instead, and the caller sees it verbatim in the
 * destructive toast `persistAlbumMutation` raises.
 */
const NO_CONNECTION_MESSAGE =
  'Няма връзка със сървъра: промяната не беше запазена. Опитайте отново.';

// ============================================
// Mappers
// ============================================

/**
 * Map the database's `photo_visibility` onto the visibility the app admits.
 *
 * `'private'` is still a legal value of the Postgres enum and still sits on
 * rows written before it was withdrawn from the UI, but it was never enforced
 * anywhere — no policy, RPC, view, trigger or function reads either
 * `visibility` column. A `'private'` row has therefore always been readable by
 * the whole family, which is exactly what `'family'` means. Mapping it to
 * `'family'` on the way in is not a downgrade of a protection; it is the first
 * time the app has told the truth about that row.
 *
 * Exported for tests — the mapping is pure and is the one place the legacy
 * value is handled.
 */
export function normalizeVisibility(
  value: Enums<'photo_visibility'>
): PhotoVisibility {
  return value === 'public' ? 'public' : 'family';
}

/**
 * Map an `albums` row to an {@link Album}.
 *
 * THE EMOJI IS DERIVED, NOT STORED. `public.albums` has no `emoji` column and
 * adding one would be a migration, so `createAlbum` and `updateAlbum` used to
 * echo the caller's emoji straight back into their return value. That looked
 * right for exactly one render: the value lived only in the object the store
 * had just been handed, and the next hydrate — a reload, a refetch, a realtime
 * echo, another device — came through here, where there was nothing to read it
 * from, and the icon vanished with no error and nothing to fix.
 *
 * It never needed a column. The picker in `CreateAlbumModal` does not offer a
 * free-form emoji: it writes `ALBUM_CATEGORY_CONFIG[category].emoji` whenever
 * the category changes, and its default `'📷'` is `ALBUM_CATEGORY_CONFIG.other
 * .emoji`. The emoji is therefore a pure function of `category`, which IS a
 * persisted column — so deriving it here reproduces exactly what the user
 * chose, and it now survives every path a row can arrive by.
 */
export function rowToAlbum(
  row: AlbumRow,
  photoCount: number,
  coverUrl?: string
): Album {
  return {
    id: row.id,
    title: row.title,
    category: row.category,
    emoji: ALBUM_CATEGORY_CONFIG[row.category].emoji,
    photoCount,
    visibility: normalizeVisibility(row.visibility),
    memberIds: [...row.member_ids],
    createdBy: row.created_by,
    isArchived: row.is_archived,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    ...(row.description != null && { description: row.description }),
    ...(row.cover_photo_id != null && { coverPhotoId: row.cover_photo_id }),
    ...(coverUrl !== undefined && coverUrl !== '' && { coverUrl }),
  };
}

/**
 * Map a `photos` row to a {@link Photo}, resolving signed URLs from `signed`.
 *
 * Synchronous and pure by design (see the module banner). `signed` is the
 * `key -> signed URL` lookup produced by {@link signPhotoRows} for the whole
 * page; a key that is missing from it — signing failed, the object is gone, the
 * policy refused — yields `''`, and the render sites show a placeholder.
 *
 * `storagePath` goes through `storageKeyFromValue` rather than being trusted
 * verbatim, so a row migration 020 did not recognise (a legacy full public URL,
 * a render URL with `?width=300`) still resolves to a signable key instead of
 * going blank forever.
 *
 * ONE KEY, ONE SIGNATURE. `photos.thumbnail_url` was mapped here until this
 * mapper stopped reading it; it held a duplicate of `url` on every row that ever
 * existed, and the fallback that used to sit on `thumbnailUrl` ("if the
 * thumbnail did not sign, use the full-size signature") was therefore taken on
 * every single row. There is nothing left for it to fall back FROM.
 *
 * The column has NOT been dropped. Migration 024 made it optional — nullable
 * with a default of `''` — precisely so that this mapper and the pre-Wave-5 one
 * are both correct against the live schema at once, which is what makes the
 * order of `db push` and `pages:deploy` stop mattering. Migration 025 drops it
 * for real and is parked in `todo/pending-migrations/` until every device is off
 * the old bundle: the old `rowToPhoto` calls `storageKeyFromValue` on
 * `row.thumbnail_url`, which starts with `value.trim()`, so a row without the
 * column is a TypeError that blanks the whole album rather than one thumbnail.
 */
export function rowToPhoto(
  row: PhotoRow,
  signed: ReadonlyMap<string, string> = NO_SIGNATURES
): Photo {
  const storagePath = storageKeyFromValue(PHOTO_BUCKET, row.url);
  const url = signed.get(storagePath) ?? '';

  return {
    id: row.id,
    albumId: row.album_id,
    storagePath,
    url,
    uploadedBy: row.uploaded_by,
    visibility: normalizeVisibility(row.visibility),
    isCover: row.is_cover,
    uploadedAt: row.uploaded_at,
    updatedAt: row.updated_at,
    ...(row.caption != null && { caption: row.caption }),
    ...(row.tags.length > 0 && { tags: [...row.tags] }),
    ...(row.tagged_members.length > 0 && {
      taggedMembers: [...row.tagged_members],
    }),
    ...(row.taken_at != null && { takenAt: row.taken_at }),
    ...(row.metadata != null && {
      metadata: row.metadata as unknown as PhotoMetadata,
    }),
  };
}

/**
 * Sign every object referenced by a page of photo rows, in ONE request.
 *
 * One key per row. It used to push both `url` and `thumbnail_url` and rely on
 * `createSignedUrls` deduping them back down — which it did, on every row,
 * because the two columns always held the same key.
 *
 * NO TRANSFORM, and this is the trade-off the whole feature rests on: the batch
 * signing endpoint has no transform parameter, so a page signed in one request
 * is signed at full size. `ALBUM_COVER_TRANSFORM` above is the one place a real
 * resize happens, on the single-object signer, where exactly one cover is being
 * signed. See the README.
 *
 * Never throws — a total failure comes back as an empty lookup and the album
 * still renders, with placeholders where the images would be.
 *
 * Exported because the realtime hook needs it for a single row.
 */
export async function signPhotoRows(
  rows: readonly PhotoRow[]
): Promise<ReadonlyMap<string, string>> {
  const paths = rows.map((row) => storageKeyFromValue(PHOTO_BUCKET, row.url));
  return createSignedUrls(PHOTO_BUCKET, paths);
}

export function rowToComment(row: PhotoCommentRow): PhotoComment {
  return {
    id: row.id,
    photoId: row.photo_id,
    text: row.text,
    authorId: row.author_id,
    createdAt: row.created_at,
    ...(row.updated_at != null && { updatedAt: row.updated_at }),
  };
}

/** True when the browser reports no network connectivity. */
function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

// ============================================
// Write-outcome helpers
// ============================================

/**
 * The {@link Album} an UNSENT update answers with.
 *
 * FABRICATED FIELDS: `familyId` (not on Album, but written as `''` into the row
 * this builds), `createdBy` (`''`), `photoCount` (**always 0**), `coverUrl`
 * (absent), `createdAt`/`updatedAt` (now), and every column the patch does not
 * carry — `title` becomes `''`, `memberIds` becomes `[]`, `isArchived` becomes
 * `false`, `coverPhotoId` becomes `null`.
 *
 * `category` is FORCED to a real value and that is not a nicety: {@link
 * rowToAlbum} derives the emoji with `ALBUM_CATEGORY_CONFIG[row.category]
 * .emoji`, so a patch without `category` — which is most of them, since
 * `updateAlbum`'s patch is built from `exactOptionalPropertyTypes` conditional
 * spreads — would dereference `undefined.emoji`. That is a runtime `TypeError`
 * inside an offline branch, i.e. a crash nothing in CI would ever reach and no
 * type error would ever warn about. `photoCount` is 0 and there is no
 * `coverUrl` for the plainer reason that both need the network that is, by
 * construction, missing.
 *
 * The store applies its own optimistic patch and discards this value. Nothing
 * may reconcile store state from it — `reconcileAlbum` exists and is already
 * wired to `createAlbum`'s return value, so wiring it to this one is a two-line
 * edit that would replace a real album with `photoCount: 0` and no cover.
 */
function synthesizeAlbum(
  albumId: string,
  patch: TablesUpdate<'albums'>
): Album {
  const now = new Date().toISOString();
  return rowToAlbum(
    {
      ...patch,
      id: albumId,
      family_id: '',
      title: patch.title ?? '',
      category: patch.category ?? 'other',
      visibility: patch.visibility ?? DEFAULT_ALBUM_SETTINGS.visibility,
      member_ids: patch.member_ids ? [...patch.member_ids] : [],
      created_by: '',
      is_archived: patch.is_archived ?? false,
      cover_photo_id: patch.cover_photo_id ?? null,
      created_at: now,
      updated_at: now,
    } as AlbumRow,
    0
  );
}

/**
 * The {@link Photo} an UNSENT update answers with.
 *
 * FABRICATED FIELDS: `storagePath` (`''`), **`url` (`''`)**, `uploadedBy`
 * (`''`), `albumId` (`''` unless the patch carries one), `uploadedAt` and
 * `updatedAt` (now), plus `tags`/`taggedMembers` (`[]`) and `isCover`
 * (`false`) whenever the patch omits them.
 *
 * `url`, `tags` and `tagged_members` are FORCED because {@link rowToPhoto}
 * dereferences all three unconditionally — `row.tags.length`,
 * `row.tagged_members.length`, and `storageKeyFromValue(PHOTO_BUCKET, row.url)`
 * which opens with `value.trim()`. A `TablesUpdate<'photos'>` leaves every one
 * of them `undefined`, so a naive cast is a runtime `TypeError` on the offline
 * path. There is no signing here either: the wire is down by construction, so
 * `url` could not be a real signed URL even if we asked for one.
 *
 * `url: ''` IS THE DANGEROUS ONE. Every render site reads an empty `url` as a
 * broken image and shows a placeholder — which is correct for a photo that has
 * never been signed, and catastrophic for a photo that has. The store applies
 * its own optimistic patch and discards this value. Nothing may reconcile store
 * state from it: `reconcilePhoto` already reconciles from `addPhoto`'s return
 * value, and pointing it at this one would blank the caption-edited photo in
 * the grid for every member of the family until the next refetch.
 */
function synthesizePhoto(
  photoId: string,
  patch: TablesUpdate<'photos'>
): Photo {
  const now = new Date().toISOString();
  return rowToPhoto({
    ...patch,
    id: photoId,
    family_id: '',
    album_id: patch.album_id ?? '',
    url: '',
    tags: patch.tags ? [...patch.tags] : [],
    tagged_members: patch.tagged_members ? [...patch.tagged_members] : [],
    uploaded_by: '',
    visibility: patch.visibility ?? DEFAULT_PHOTO_SETTINGS.visibility,
    is_cover: patch.is_cover ?? false,
    uploaded_at: now,
    updated_at: now,
  } as PhotoRow);
}

/** What a DELETE that reported zero rows turned out to mean. */
type ZeroRowDeleteVerdict = 'already-gone' | 'refused' | 'transient';

/**
 * Decide what a zero-row DELETE meant, from ONE confirming SELECT of the row.
 *
 * A DELETE THAT AFFECTED NO ROWS IS AMBIGUOUS, and resolving that ambiguity is
 * the reason every delete on this service now pays for a second round trip on
 * the rare path. `.select('id')` on a delete returns the rows it removed; an
 * empty array means either the policy refused the write — an RLS `USING` clause
 * FILTERS rather than raises, so PostgREST answers with `error === null` and no
 * rows — or the row was already gone because another member of the family
 * deleted it first from their own device.
 *
 * Treating both as failure was the shape this wave started with, and it is
 * wrong in a way that is worse than the bug it replaces: `albumStore`'s
 * rollback RE-INSERTS the album, its photo and its comments. The user would get
 * a destructive toast and a phantom row back for a delete that had completely
 * succeeded. Treating both as success is the bug itself.
 *
 * So we ask. On all five tables this wave touches, SELECT is family-wide and
 * every delete permission is strictly narrower — `albums` and `photos` reach
 * that by different routes (`albums_modify_parent` is a `FOR ALL` policy that
 * supplies the DELETE `USING` clause, while `photos` has dedicated
 * `photos_delete_own` / `photos_delete_parent` policies), but the effect is the
 * same and it is what makes the question answerable at all: a row that is still
 * READABLE after a delete that removed nothing can only have been refused.
 *
 * A row that is no longer readable is gone, which is exactly what the user
 * asked for, so the delete resolves with no toast and no rollback. THIS PUTS
 * THE LIVE PATH IN AGREEMENT WITH THE REPLAY PATH rather than in contradiction
 * with it: `replaySupabaseMutation` in `src/shared/hooks/useOfflineQueue.ts`
 * deliberately returns `success` for a zero-row replay DELETE — and routes a
 * zero-row replay UPDATE to {@link zeroRowOutcome} as a permanent failure, the
 * same asymmetry the update paths in this file keep. Do not "fix" one of them
 * to match the other.
 *
 * If the confirming SELECT itself fails we are back to knowing nothing, so it
 * is classified like any other write failure and the caller decides: a
 * transport class it can queue (where it has a queue at all), and anything
 * else surfaced as the refusal. Surfacing a failure is always the safe
 * direction; swallowing one is what this wave exists to stop.
 */
function classifyZeroRowDelete(
  stillReadable: boolean,
  error: { readonly message?: string } | null,
  status: number | undefined
): ZeroRowDeleteVerdict {
  if (error) {
    return isTransientWriteFailure(status, error) ? 'transient' : 'refused';
  }
  return stillReadable ? 'refused' : 'already-gone';
}

// ============================================
// Supabase Album Service
// ============================================

export class SupabaseAlbumService implements IAlbumService {
  private get client() {
    return getSupabaseClient();
  }

  // =====================
  // Offline enqueue paths
  // =====================
  //
  // One per operation, so the `navigator.onLine === false` gate and the
  // transport-failure fallback below it queue byte-identical entries. They were
  // inline before, which meant only the first of the two could exist.

  /** Queue an album INSERT for replay and answer with the optimistic album. */
  private async enqueueAlbumInsert(
    insert: TablesInsert<'albums'>
  ): Promise<Album> {
    const now = new Date().toISOString();
    const payload: TablesInsert<'albums'> = {
      ...insert,
      id: crypto.randomUUID(),
      created_at: now,
      updated_at: now,
    };
    await enqueueSupabaseMutation({
      table: 'albums',
      op: 'insert',
      payload,
      description: `Създаване на албум „${insert.title}“`,
    });
    // The optimistic album goes through the same mapper as a fetched row, so
    // the queued album shows the same category emoji it will show after replay.
    return rowToAlbum(payload as AlbumRow, 0);
  }

  /** Queue an album UPDATE for replay. */
  private async enqueueAlbumUpdate(
    albumId: string,
    patch: TablesUpdate<'albums'>
  ): Promise<void> {
    await enqueueSupabaseMutation({
      table: 'albums',
      op: 'update',
      payload: patch,
      matchId: albumId,
      description: 'Промяна на албум',
    });
  }

  /** Queue an album DELETE for replay. */
  private async enqueueAlbumDelete(albumId: string): Promise<void> {
    await enqueueSupabaseMutation({
      table: 'albums',
      op: 'delete',
      matchId: albumId,
      description: 'Изтриване на албум',
    });
  }

  /**
   * Queue a photo-row INSERT for replay and answer with the optimistic photo.
   *
   * No signing on this path: it is only reached with the write already failing
   * on the wire, so a sign request would fail too. The returned Photo carries
   * its storage keys and empty URLs; the store's optimistic entry already holds
   * `previewUrl` for the render, and the refetch after replay is what finally
   * produces real signatures. The album `updated_at` bump is skipped for the
   * same reason and is reconciled by that refetch.
   */
  private async enqueuePhotoInsert(
    insert: TablesInsert<'photos'>
  ): Promise<Photo> {
    const now = new Date().toISOString();
    const payload: TablesInsert<'photos'> = {
      ...insert,
      id: crypto.randomUUID(),
      uploaded_at: now,
      updated_at: now,
    };
    await enqueueSupabaseMutation({
      table: 'photos',
      op: 'insert',
      payload,
      description: 'Добавяне на снимка',
    });
    return rowToPhoto(payload as PhotoRow);
  }

  /** Queue a photo UPDATE for replay. */
  private async enqueuePhotoUpdate(
    photoId: string,
    patch: TablesUpdate<'photos'>
  ): Promise<void> {
    await enqueueSupabaseMutation({
      table: 'photos',
      op: 'update',
      payload: patch,
      matchId: photoId,
      description: 'Промяна на снимка',
    });
  }

  /**
   * Queue a photo DELETE for replay.
   *
   * The cover-clearing fix-up needs live reads and is skipped on this path; it
   * is reconciled by the refetch that follows replay.
   */
  private async enqueuePhotoDelete(photoId: string): Promise<void> {
    await enqueueSupabaseMutation({
      table: 'photos',
      op: 'delete',
      matchId: photoId,
      description: 'Изтриване на снимка',
    });
  }

  // =====================
  // Album Operations
  // =====================

  async getAlbums(
    filters?: AlbumFilters,
    sortBy: AlbumSortBy = 'updatedAt',
    sortDirection: SortDirection = 'desc'
  ): Promise<Album[]> {
    const familyId = await getCurrentFamilyId();
    const [{ data: albumRows, error: albumError }, { data: photoRows }] =
      await Promise.all([
        this.client.from('albums').select('*').eq('family_id', familyId),
        this.client
          .from('photos')
          .select('id, album_id, url')
          .eq('family_id', familyId),
      ]);
    if (albumError) {
      throw new Error(albumError.message);
    }

    const countByAlbum = new Map<string, number>();
    const keyByPhotoId = new Map<string, string>();
    for (const p of photoRows ?? []) {
      countByAlbum.set(p.album_id, (countByAlbum.get(p.album_id) ?? 0) + 1);
      keyByPhotoId.set(p.id, storageKeyFromValue(PHOTO_BUCKET, p.url));
    }

    // Sign every cover in ONE request. Only the covers — signing all photos of
    // every album to fill in a grid of album cards would be the same N+1 in a
    // different costume, just paid up front.
    const coverKeys = (albumRows ?? [])
      .map((row) =>
        row.cover_photo_id != null ? keyByPhotoId.get(row.cover_photo_id) : ''
      )
      .filter((key): key is string => key !== undefined);
    const signedCovers = await createSignedUrls(PHOTO_BUCKET, coverKeys);

    let result: Album[] = (albumRows ?? []).map((row) => {
      const coverKey =
        row.cover_photo_id != null
          ? keyByPhotoId.get(row.cover_photo_id)
          : undefined;
      const coverUrl =
        coverKey !== undefined ? signedCovers.get(coverKey) : undefined;
      return rowToAlbum(row, countByAlbum.get(row.id) ?? 0, coverUrl);
    });

    // Filter client-side — the query above is scoped by family only.
    if (filters) {
      if (!filters.showArchived) {
        result = result.filter((a) => !a.isArchived);
      }
      if (filters.category) {
        result = result.filter((a) => a.category === filters.category);
      }
      if (filters.visibility) {
        result = result.filter((a) => a.visibility === filters.visibility);
      }
      if (filters.search) {
        const search = filters.search.toLowerCase();
        result = result.filter(
          (a) =>
            a.title.toLowerCase().includes(search) ||
            a.description?.toLowerCase().includes(search)
        );
      }
    }

    // Sort client-side, so the comparator can use the derived fields.
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'title':
          comparison = a.title.localeCompare(b.title, 'bg');
          break;
        case 'createdAt':
          comparison =
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case 'updatedAt':
          comparison =
            new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          break;
        case 'photoCount':
          comparison = a.photoCount - b.photoCount;
          break;
        default:
          comparison = 0;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }

  async getAlbum(albumId: string): Promise<Album | undefined> {
    const familyId = await getCurrentFamilyId();
    const { data, error } = await this.client
      .from('albums')
      .select('*')
      .eq('family_id', familyId)
      .eq('id', albumId)
      .maybeSingle();
    if (error || !data) {
      return undefined;
    }
    const photoCount = await this.countPhotos(familyId, albumId);
    const coverUrl = await this.resolveCoverUrl(familyId, data.cover_photo_id);
    return rowToAlbum(data, photoCount, coverUrl);
  }

  private async countPhotos(
    familyId: string,
    albumId: string
  ): Promise<number> {
    const { count } = await this.client
      .from('photos')
      .select('id', { count: 'exact', head: true })
      .eq('family_id', familyId)
      .eq('album_id', albumId);
    return count ?? 0;
  }

  /**
   * Signed URL for one album's cover, resized for the card it renders in.
   *
   * Single-object path, so it can afford the `transform` the batch endpoint
   * cannot carry. Signing failures return `undefined` rather than propagating:
   * a cover that will not sign must cost the card its image, not the caller its
   * whole album fetch.
   */
  private async resolveCoverUrl(
    familyId: string,
    coverPhotoId: string | null
  ): Promise<string | undefined> {
    if (!coverPhotoId) {
      return undefined;
    }
    const { data } = await this.client
      .from('photos')
      .select('url')
      .eq('family_id', familyId)
      .eq('id', coverPhotoId)
      .maybeSingle();

    const key = storageKeyFromValue(PHOTO_BUCKET, data?.url ?? '');
    if (key === '') {
      return undefined;
    }

    try {
      return await createSignedUrl(PHOTO_BUCKET, key, SIGNED_URL_TTL_SECONDS, {
        transform: ALBUM_COVER_TRANSFORM,
      });
    } catch {
      return undefined;
    }
  }

  async createAlbum(
    input: CreateAlbumInput,
    createdBy: string
  ): Promise<Album> {
    const familyId = await getCurrentFamilyId();
    const insert: TablesInsert<'albums'> = {
      family_id: familyId,
      title: sanitizeText(input.title),
      category: input.category,
      visibility: input.visibility ?? DEFAULT_ALBUM_SETTINGS.visibility,
      member_ids: input.memberIds ? [...input.memberIds] : [],
      created_by: createdBy,
      is_archived: false,
      ...(input.description !== undefined && {
        description: sanitizeText(input.description),
      }),
    };

    // Offline: persist the insert for replay and return an optimistic album.
    if (isOffline()) {
      return this.enqueueAlbumInsert(insert);
    }

    const { data, error, status } = await this.client
      .from('albums')
      .insert(insert)
      .select('*')
      .single();
    if (error || !data) {
      // The browser said "online" and the write still never landed — queue it
      // rather than losing it. A refusal falls through and throws.
      if (isTransientWriteFailure(status, error)) {
        return this.enqueueAlbumInsert(insert);
      }
      throw new Error(error?.message ?? 'Неуспешно създаване на албум.');
    }
    return rowToAlbum(data, 0);
  }

  /**
   * Apply a patch to an album, and answer only with an album that is safe.
   *
   * A RESOLVED VALUE MEANS THE PATCH LANDED IN POSTGRES OR IS ON THE OFFLINE
   * QUEUE. Nothing else resolves. This method used to return `Album | null` and
   * hand `null` back for a refusal, which `persistAlbumMutation` — a `.catch()`
   * and nothing else — could not distinguish from success. A child renaming a
   * shared album got a silent `null`: no toast, no rollback, and the new title
   * on screen until the next focus refetch replaced it with the old one.
   */
  async updateAlbum(
    albumId: string,
    updates: UpdateAlbumInput
  ): Promise<Album> {
    const patch: TablesUpdate<'albums'> = {
      ...(updates.title !== undefined && {
        title: sanitizeText(updates.title),
      }),
      ...(updates.description !== undefined && {
        description:
          updates.description === undefined
            ? null
            : sanitizeText(updates.description),
      }),
      ...(updates.category !== undefined && { category: updates.category }),
      ...(updates.coverPhotoId !== undefined && {
        cover_photo_id: updates.coverPhotoId ?? null,
      }),
      ...(updates.visibility !== undefined && {
        visibility: updates.visibility,
      }),
      ...(updates.memberIds !== undefined && {
        member_ids: [...updates.memberIds],
      }),
      ...(updates.isArchived !== undefined && {
        is_archived: updates.isArchived,
      }),
    };

    const REFUSED_MESSAGE =
      'Албумът не можа да бъде променен: нямате права или вече е изтрит.';

    // Offline: queue the patch for replay and answer with the album the patch
    // describes. The store applies updates optimistically and discards this
    // return value — see {@link synthesizeAlbum} for what is fabricated in it
    // and why nothing may reconcile from it. The family scope is resolved BELOW
    // the gate: the queued entry matches on the primary key alone and never
    // needed it.
    if (isOffline()) {
      await this.enqueueAlbumUpdate(albumId, patch);
      return synthesizeAlbum(albumId, patch);
    }

    const familyId = await getCurrentFamilyId();
    const { data, error, status } = await this.client
      .from('albums')
      .update(patch)
      .eq('family_id', familyId)
      .eq('id', albumId)
      .select('*')
      .maybeSingle();
    // `error === null` with no row is an RLS-filtered UPDATE, not a wire
    // failure — it must never be queued, so the classifier is only consulted
    // when there is an error envelope to classify.
    if (error && isTransientWriteFailure(status, error)) {
      await this.enqueueAlbumUpdate(albumId, patch);
      return synthesizeAlbum(albumId, patch);
    }
    // The refusal and the zero-row case are one message on purpose: unlike a
    // delete, an UPDATE has no cheap way to tell "the policy filtered you out"
    // from "the row is gone", and there is no version of this the user can act
    // on differently. Both mean the album on screen is not the album in the
    // database, and the store's rollback is about to prove it.
    if (error) {
      throw new Error(REFUSED_MESSAGE, { cause: error });
    }
    if (!data) {
      throw new Error(REFUSED_MESSAGE);
    }
    const photoCount = await this.countPhotos(familyId, albumId);
    const coverUrl = await this.resolveCoverUrl(familyId, data.cover_photo_id);
    // No emoji echo: the mapper derives it from the category that was just
    // written, so an update that CHANGES the category also changes the icon —
    // which is what the picker in `CreateAlbumModal` shows while editing.
    return rowToAlbum(data, photoCount, coverUrl);
  }

  /**
   * Delete an album, its photo rows (by cascade) and their storage objects.
   *
   * THE KEYS ARE READ BEFORE THE ROWS ARE DELETED, and that ordering is the
   * whole reason this method has a pre-read at all. `photos.album_id` is
   * `on delete cascade`, so the instant the album row goes every key it owned
   * is unrecoverable from the database — the objects would survive as orphans
   * that nothing in the app can name, reclaimable only by
   * cleanup-orphan-storage's bucket walk.
   *
   * The removal itself runs AFTER the row delete succeeds and is best-effort:
   * see {@link removeFiles}. The album is what the user asked to delete.
   *
   * "AFTER IT SUCCEEDS" USED TO BE A LIE, and this is the data loss this method
   * was rewritten to stop. The delete was issued bare — no `.select()` — so an
   * RLS-refused delete came back as `{ error: null }`, fell straight past
   * `if (error) return false`, and reached `removeFiles` with `keys` holding
   * every object the pre-read had just collected. The album rows all survived,
   * because the delete really had been refused. The objects did not.
   *
   * The live scope was bounded by storage's own policy rather than by anything
   * in this file: `storage_delete_owner_or_parent` allows a delete when the
   * object is in the caller's family folder AND (`owner = auth.uid()` OR the
   * caller is a parent). So a CHILD tapping delete on a parent's album — which
   * `AlbumsPage` gates on nothing but a `confirm()` — destroyed exactly the
   * objects that child had uploaded and was refused the rest. The result was an
   * album that still existed, still listed every photo, and rendered permanent
   * placeholders for the ones that child contributed, with no error and no way
   * for anyone to fix it. The zero-row check below therefore sits STRICTLY
   * ABOVE `removeFiles`, and must stay there.
   */
  async deleteAlbum(albumId: string): Promise<void> {
    // Offline: queue the delete for replay; report optimistic success.
    //
    // No storage work on this path. The objects cannot be removed without the
    // network that is missing, the keys would have to be carried in the queued
    // entry to be removed later, and a queue entry that names objects to delete
    // is a delete instruction sitting in IndexedDB across a session boundary.
    // The album's objects become orphans and cleanup-orphan-storage reclaims
    // them; that is exactly the case it exists for.
    if (isOffline()) {
      await this.enqueueAlbumDelete(albumId);
      return;
    }
    const familyId = await getCurrentFamilyId();

    const keys = await this.collectAlbumPhotoKeys(familyId, albumId);

    const REFUSED_MESSAGE = 'Албумът не можа да бъде изтрит: нямате права.';

    // `.select('id')` is what turns "the request did not error" into "the row
    // is gone". Without it a refused delete is indistinguishable from a
    // successful one on the wire.
    const { data, error, status } = await this.client
      .from('albums')
      .delete()
      .eq('family_id', familyId)
      .eq('id', albumId)
      .select('id');
    if (error && isTransientWriteFailure(status, error)) {
      await this.enqueueAlbumDelete(albumId);
      return;
    }
    if (error) {
      // The rows are still there and still reference these objects. Removing
      // them now would delete live photos out from under an album that still
      // exists — the one case where "best effort" would be actively wrong.
      throw new Error(REFUSED_MESSAGE, { cause: error });
    }
    if (!data || data.length === 0) {
      // Same reasoning as the branch above, for the case that USED to fall
      // through to `removeFiles` and empty the album: no error envelope, no
      // rows, and the album still sitting in Postgres. See
      // {@link classifyZeroRowDelete} for why one extra SELECT can tell a
      // refusal from a row another device deleted first.
      const {
        data: survivor,
        error: confirmError,
        status: confirmStatus,
      } = await this.client
        .from('albums')
        .select('id')
        .eq('family_id', familyId)
        .eq('id', albumId)
        .maybeSingle();
      const verdict = classifyZeroRowDelete(
        survivor !== null,
        confirmError,
        confirmStatus
      );
      if (verdict === 'transient') {
        await this.enqueueAlbumDelete(albumId);
        return;
      }
      if (verdict === 'refused') {
        throw new Error(REFUSED_MESSAGE);
      }
      // 'already-gone': another member deleted this album first, so its photo
      // rows went with it by cascade and the keys the pre-read collected now
      // name nothing. Falling through to the removal reclaims them; it is the
      // one case where these objects are provably unreferenced.
    }

    await removeFiles(PHOTO_BUCKET, keys);
  }

  /**
   * Every storage key the album's photos reference, for the delete path.
   *
   * Failure yields an EMPTY list, never a throw: a lookup that did not answer
   * must not stop the album delete the user asked for. The cost of the empty
   * case is orphaned objects, which cleanup-orphan-storage reclaims — the cost
   * of throwing would be an album the user cannot delete because a SELECT
   * timed out.
   *
   * Keys go through {@link storageKeyForRemoval}, which refuses an ambiguous
   * value outright rather than cutting it at a '?' — on a delete path a
   * mis-parsed key names a real object belonging to someone else.
   *
   * NOT PAGED, and that is a bounded, stated shortfall rather than an oversight.
   * PostgREST trims every response to the server's `max_rows`, so an album
   * holding more photos than that cap yields only the first page and the rest of
   * its objects are left behind. Paging here would mean a keyset loop and an
   * abort policy — the machinery `referencePaging.ts` exists to hold — for a job
   * whose entire purpose is to save cleanup-orphan-storage some work. The
   * leftovers are exactly what that function reclaims.
   */
  private async collectAlbumPhotoKeys(
    familyId: string,
    albumId: string
  ): Promise<readonly string[]> {
    const { data, error } = await this.client
      .from('photos')
      .select('url')
      .eq('family_id', familyId)
      .eq('album_id', albumId);
    if (error || !data) {
      return [];
    }
    return data
      .map((row) => storageKeyForRemoval(PHOTO_BUCKET, row.url))
      .filter((key) => key !== '');
  }

  /**
   * Archive an album.
   *
   * The body is `updateAlbum`'s, unchanged, and that IS the mechanism: the
   * delegate's contract is the delegator's contract, so a refusal throws here
   * for exactly the same reason it throws there. No try/catch, no unwrap, no
   * rewrap — anything of the kind would put a second, divergent opinion about
   * what a failed write means into a method that has none of its own.
   */
  async archiveAlbum(albumId: string): Promise<Album> {
    return this.updateAlbum(albumId, { isArchived: true });
  }

  /** Un-archive an album. Delegates exactly as {@link archiveAlbum} does. */
  async restoreAlbum(albumId: string): Promise<Album> {
    return this.updateAlbum(albumId, { isArchived: false });
  }

  // =====================
  // Photo Operations
  // =====================

  async getPhotos(albumId: string): Promise<Photo[]> {
    const familyId = await getCurrentFamilyId();
    const { data, error } = await this.client
      .from('photos')
      .select('*')
      .eq('family_id', familyId)
      .eq('album_id', albumId)
      .order('uploaded_at', { ascending: true });
    if (error) {
      throw new Error(error.message);
    }
    const rows = data ?? [];
    const signed = await signPhotoRows(rows);
    return rows.map((row) => rowToPhoto(row, signed));
  }

  async getAllPhotos(): Promise<Photo[]> {
    const familyId = await getCurrentFamilyId();
    const { data, error } = await this.client
      .from('photos')
      .select('*')
      .eq('family_id', familyId)
      .order('uploaded_at', { ascending: true });
    if (error) {
      throw new Error(error.message);
    }
    const rows = data ?? [];
    const signed = await signPhotoRows(rows);
    return rows.map((row) => rowToPhoto(row, signed));
  }

  async getPhoto(photoId: string): Promise<Photo | undefined> {
    const familyId = await getCurrentFamilyId();
    const { data, error } = await this.client
      .from('photos')
      .select('*')
      .eq('family_id', familyId)
      .eq('id', photoId)
      .maybeSingle();
    if (error || !data) {
      return undefined;
    }
    // One row, but still the batch signer: its two columns are usually the same
    // key, so this is one request either way and there is one code path to get
    // wrong instead of two.
    return rowToPhoto(data, await signPhotoRows([data]));
  }

  /**
   * Insert a photo ROW. The binary is already in the bucket — PhotoUploader put
   * it there and handed over the object key.
   *
   * `input.url` is a RAW KEY and is normalised through `storageKeyFromValue`
   * before it is written, so a caller that still hands over a full public URL
   * stores the key rather than the URL. When the value is unrecognisable the
   * original is persisted verbatim: the column is `not null`, and writing `''`
   * would trade a bad value for an invalid row.
   *
   * `input.previewUrl` is display-only and is deliberately NOT part of the
   * insert. A signed URL must never reach a column — it expires, and the
   * offline branch below persists this payload into IndexedDB where it could
   * sit for days before replay.
   */
  async addPhoto(input: AddPhotoInput, uploadedBy: string): Promise<Photo> {
    const familyId = await getCurrentFamilyId();
    const storageKey =
      storageKeyFromValue(PHOTO_BUCKET, input.url) || input.url;
    const insert: TablesInsert<'photos'> = {
      family_id: familyId,
      album_id: input.albumId,
      url: storageKey,
      visibility: input.visibility ?? DEFAULT_PHOTO_SETTINGS.visibility,
      is_cover: DEFAULT_PHOTO_SETTINGS.isCover,
      uploaded_by: uploadedBy,
      tags: input.tags ? [...input.tags] : [],
      tagged_members: input.taggedMembers ? [...input.taggedMembers] : [],
      ...(input.caption !== undefined && {
        caption: sanitizeText(input.caption),
      }),
      ...(input.takenAt !== undefined && { taken_at: input.takenAt }),
      ...(input.metadata !== undefined && {
        metadata: input.metadata as unknown as Json,
      }),
    };

    // Offline: persist the insert for replay and return an optimistic photo.
    // Rarely taken in practice — the only caller path runs PhotoUploader
    // first, and `input.url` exists only because its binary upload already
    // reached storage, so this branch needs the connection to drop between
    // that upload and this insert.
    if (isOffline()) {
      return this.enqueuePhotoInsert(insert);
    }

    const { data, error, status } = await this.client
      .from('photos')
      .insert(insert)
      .select('*')
      .single();
    if (error || !data) {
      // The browser said "online" and the write still never landed — queue it
      // rather than losing it. A refusal falls through and throws.
      if (isTransientWriteFailure(status, error)) {
        return this.enqueuePhotoInsert(insert);
      }
      throw new Error(error?.message ?? 'Неуспешно добавяне на снимка.');
    }

    // Bump the album's updatedAt (photoCount is derived, not stored).
    await this.client
      .from('albums')
      .update({ updated_at: new Date().toISOString() })
      .eq('family_id', familyId)
      .eq('id', input.albumId);

    return rowToPhoto(data, await signPhotoRows([data]));
  }

  /**
   * Apply a patch to a photo, and answer only with a photo that is safe.
   *
   * Same contract as {@link updateAlbum}: a resolved value means the patch is
   * in Postgres or on the offline queue, and every refusal throws. `photos` has
   * dedicated `photos_update_own` / `photos_update_parent` policies, so a child
   * editing another member's caption is precisely the case that used to come
   * back as a silent `null`.
   */
  async updatePhoto(
    photoId: string,
    updates: UpdatePhotoInput
  ): Promise<Photo> {
    const patch: TablesUpdate<'photos'> = {
      ...(updates.caption !== undefined && {
        caption: sanitizeText(updates.caption),
      }),
      ...(updates.tags !== undefined && { tags: [...updates.tags] }),
      ...(updates.taggedMembers !== undefined && {
        tagged_members: [...updates.taggedMembers],
      }),
      ...(updates.visibility !== undefined && {
        visibility: updates.visibility,
      }),
      ...(updates.isCover !== undefined && { is_cover: updates.isCover }),
    };

    const REFUSED_MESSAGE =
      'Снимката не можа да бъде променена: нямате права или вече е изтрита.';

    // Offline: queue the patch for replay and answer with the photo the patch
    // describes. The store applies updates optimistically and discards this
    // return value — see {@link synthesizePhoto}, whose `url: ''` is precisely
    // what a render site shows as a broken image if anything ever reconciles
    // from it. The family scope is resolved BELOW the gate: the queued entry
    // matches on the primary key alone and never needed it.
    if (isOffline()) {
      await this.enqueuePhotoUpdate(photoId, patch);
      return synthesizePhoto(photoId, patch);
    }

    const familyId = await getCurrentFamilyId();
    const { data, error, status } = await this.client
      .from('photos')
      .update(patch)
      .eq('family_id', familyId)
      .eq('id', photoId)
      .select('*')
      .maybeSingle();
    // `error === null` with no row is an RLS-filtered UPDATE, not a wire
    // failure — it must never be queued, so the classifier is only consulted
    // when there is an error envelope to classify.
    if (error && isTransientWriteFailure(status, error)) {
      await this.enqueuePhotoUpdate(photoId, patch);
      return synthesizePhoto(photoId, patch);
    }
    if (error) {
      throw new Error(REFUSED_MESSAGE, { cause: error });
    }
    if (!data) {
      throw new Error(REFUSED_MESSAGE);
    }
    return rowToPhoto(data, await signPhotoRows([data]));
  }

  /**
   * Delete a photo row and, once it is gone, its storage object.
   *
   * The pre-read already existed to find `album_id` for the cover fix-up below;
   * it now also carries `url`, because after the row is deleted there is nothing
   * left in the database that names the object. One extra column on a query
   * that was already being issued.
   *
   * The object is removed AFTER the row, never before, and best-effort — see
   * {@link removeFiles}. Removing first would mean a failed delete leaves a live
   * row pointing at a missing object, which renders as a permanent placeholder
   * in the album with no way for the user to fix it.
   *
   * That is exactly what a refused delete USED to do, by a different route:
   * the delete was issued bare, so an RLS refusal arrived as `{ error: null }`,
   * skipped `if (error) return false`, and removed the object under a row that
   * was still there. THE BLAST RADIUS HERE WAS NARROWER THAN `deleteAlbum`'s,
   * and only by luck: the row policies (`photos_delete_own` on
   * `uploaded_by = auth.uid()`, `photos_delete_parent` on `is_parent()`) and
   * the storage policy (`owner = auth.uid()` OR `is_parent()`) happen to line
   * up almost exactly, so a caller refused the row was usually refused the
   * object too. `deleteAlbum` has no such luck — deleting an album needs
   * `is_parent()` while deleting the objects needs only ownership — which is
   * why that method is where the damage actually landed. The structure was the
   * same bug in both, so the fix is the same: the zero-row check below sits
   * STRICTLY ABOVE both the removal and the cover fix-up, and must stay there.
   *
   * TWO DIFFERENT REFUSALS LIVE IN HERE and they deliberately read differently.
   * The pre-read finding no photo means the row is not there to delete; a
   * zero-row delete of a row that is still readable means the policy said no.
   * Collapsing them into one message would tell a parent they lack permission
   * for a photo that simply no longer exists.
   */
  async deletePhoto(photoId: string): Promise<void> {
    // Offline: queue the delete for replay; report optimistic success. No
    // storage work — same reasoning as `deleteAlbum`'s offline branch.
    if (isOffline()) {
      await this.enqueuePhotoDelete(photoId);
      return;
    }

    const NOT_FOUND_MESSAGE =
      'Снимката не е намерена: може да е изтрита от друг член на семейството.';
    const REFUSED_MESSAGE = 'Снимката не можа да бъде изтрита: нямате права.';

    const familyId = await getCurrentFamilyId();
    const {
      data: photo,
      error: lookupError,
      status: lookupStatus,
    } = await this.client
      .from('photos')
      .select('album_id, url')
      .eq('family_id', familyId)
      .eq('id', photoId)
      .maybeSingle();
    // The lookup is the first thing on the wire, so a dead uplink shows up here
    // rather than on the delete. Queue on that, instead of failing a photo the
    // user will still see gone from the grid.
    if (lookupError && isTransientWriteFailure(lookupStatus, lookupError)) {
      await this.enqueuePhotoDelete(photoId);
      return;
    }
    if (lookupError) {
      throw new Error(NOT_FOUND_MESSAGE, { cause: lookupError });
    }
    if (!photo) {
      throw new Error(NOT_FOUND_MESSAGE);
    }

    const { data, error, status } = await this.client
      .from('photos')
      .delete()
      .eq('family_id', familyId)
      .eq('id', photoId)
      .select('id');
    if (error && isTransientWriteFailure(status, error)) {
      await this.enqueuePhotoDelete(photoId);
      return;
    }
    if (error) {
      throw new Error(REFUSED_MESSAGE, { cause: error });
    }
    if (!data || data.length === 0) {
      // No envelope and no rows: either the policy filtered the delete out, or
      // the row went away between the pre-read above and this statement. One
      // confirming SELECT decides — see {@link classifyZeroRowDelete}.
      const {
        data: survivor,
        error: confirmError,
        status: confirmStatus,
      } = await this.client
        .from('photos')
        .select('id')
        .eq('family_id', familyId)
        .eq('id', photoId)
        .maybeSingle();
      const verdict = classifyZeroRowDelete(
        survivor !== null,
        confirmError,
        confirmStatus
      );
      if (verdict === 'transient') {
        await this.enqueuePhotoDelete(photoId);
        return;
      }
      if (verdict === 'refused') {
        throw new Error(REFUSED_MESSAGE);
      }
      // 'already-gone': another device deleted this row first. Its object is
      // unreferenced either way, and the cover fix-up below is idempotent, so
      // both run exactly as they would on our own successful delete.
    }

    // The row is PROVABLY gone — either this statement removed it or the
    // confirming SELECT above found it already absent. Nothing references the
    // object any more, so reclaim it.
    await removeFiles(PHOTO_BUCKET, [
      storageKeyForRemoval(PHOTO_BUCKET, photo.url),
    ]);

    // Clear the album cover if this photo was the cover.
    const { data: album } = await this.client
      .from('albums')
      .select('cover_photo_id')
      .eq('family_id', familyId)
      .eq('id', photo.album_id)
      .maybeSingle();
    if (album && album.cover_photo_id === photoId) {
      await this.client
        .from('albums')
        .update({ cover_photo_id: null })
        .eq('family_id', familyId)
        .eq('id', photo.album_id);
    }
  }

  /**
   * Point an album at one of its own photos as the cover.
   *
   * NO OFFLINE QUEUE BRANCH, and that is a stated shortfall rather than an
   * oversight: Wave 4 gave the other album writes an `isOffline()` gate and a
   * transient fallback and never reached this one. Adding one is new behaviour
   * and is not this change's business.
   *
   * What this change does owe the user is honesty about which failure they hit.
   * Every refusal here now throws, and a throw lands verbatim in a destructive
   * toast — so a transport failure must NOT borrow the permission wording. A
   * parent on the underground would be told they lack rights they have. Both
   * queries therefore classify their own failure and route the transport class
   * to {@link NO_CONNECTION_MESSAGE}, which is why both destructure `status`.
   */
  async setAlbumCover(albumId: string, photoId: string): Promise<Album> {
    const familyId = await getCurrentFamilyId();

    const PHOTO_NOT_IN_ALBUM_MESSAGE = 'Снимката не е намерена в този албум.';
    const REFUSED_MESSAGE =
      'Корицата не можа да бъде променена: нямате права или албумът вече е изтрит.';

    const {
      data: photo,
      error: lookupError,
      status: lookupStatus,
    } = await this.client
      .from('photos')
      .select('id')
      .eq('family_id', familyId)
      .eq('id', photoId)
      .eq('album_id', albumId)
      .maybeSingle();
    // Without this the offline case reads as a missing photo, and the user is
    // told their photo is not in the album they are looking at it in.
    if (lookupError && isTransientWriteFailure(lookupStatus, lookupError)) {
      throw new Error(NO_CONNECTION_MESSAGE, { cause: lookupError });
    }
    if (lookupError) {
      throw new Error(PHOTO_NOT_IN_ALBUM_MESSAGE, { cause: lookupError });
    }
    if (!photo) {
      throw new Error(PHOTO_NOT_IN_ALBUM_MESSAGE);
    }

    const {
      data: album,
      error,
      status,
    } = await this.client
      .from('albums')
      .update({ cover_photo_id: photoId })
      .eq('family_id', familyId)
      .eq('id', albumId)
      .select('*')
      .maybeSingle();
    if (error && isTransientWriteFailure(status, error)) {
      throw new Error(NO_CONNECTION_MESSAGE, { cause: error });
    }
    // `albums_modify_parent` is `FOR ALL (family AND is_parent())`, so this is
    // the statement a child's cover change is filtered out by — with no error
    // envelope and no row, which is why `!album` has to be a refusal and not a
    // shrug.
    if (error) {
      throw new Error(REFUSED_MESSAGE, { cause: error });
    }
    if (!album) {
      throw new Error(REFUSED_MESSAGE);
    }

    // Reset all covers in this album, then set the new one. DELIBERATELY
    // UNCHECKED: `photos.is_cover` is cosmetic, the cover itself is already
    // committed on the album row above, and a refusal can no longer reach here
    // at all — the update above throws first. Do not "fix" these into checked
    // writes; there is no outcome they could report that the caller could act
    // on.
    await this.client
      .from('photos')
      .update({ is_cover: false })
      .eq('family_id', familyId)
      .eq('album_id', albumId);
    await this.client
      .from('photos')
      .update({ is_cover: true })
      .eq('family_id', familyId)
      .eq('id', photoId);

    const photoCount = await this.countPhotos(familyId, albumId);
    const coverUrl = await this.resolveCoverUrl(familyId, photoId);
    return rowToAlbum(album, photoCount, coverUrl);
  }

  // =====================
  // Comment Operations
  // =====================

  async getComments(photoId: string): Promise<PhotoComment[]> {
    const familyId = await getCurrentFamilyId();
    const { data, error } = await this.client
      .from('photo_comments')
      .select('*')
      .eq('family_id', familyId)
      .eq('photo_id', photoId)
      .order('created_at', { ascending: true });
    if (error) {
      throw new Error(error.message);
    }
    return (data ?? []).map(rowToComment);
  }

  async getAllComments(): Promise<PhotoComment[]> {
    const familyId = await getCurrentFamilyId();
    const { data, error } = await this.client
      .from('photo_comments')
      .select('*')
      .eq('family_id', familyId)
      .order('created_at', { ascending: true });
    if (error) {
      throw new Error(error.message);
    }
    return (data ?? []).map(rowToComment);
  }

  async addComment(
    photoId: string,
    text: string,
    authorId: string
  ): Promise<PhotoComment> {
    const familyId = await getCurrentFamilyId();
    const insert: TablesInsert<'photo_comments'> = {
      family_id: familyId,
      photo_id: photoId,
      text: sanitizeText(text),
      author_id: authorId,
    };
    const { data, error } = await this.client
      .from('photo_comments')
      .insert(insert)
      .select('*')
      .single();
    if (error || !data) {
      throw new Error(error?.message ?? 'Неуспешно добавяне на коментар.');
    }
    return rowToComment(data);
  }

  /**
   * Delete one comment.
   *
   * `return !error` was the most compact statement of this wave's bug in the
   * whole codebase. `photo_comments`'s DELETE policy allows the author or a
   * parent; anyone else is FILTERED, which PostgREST reports as success with an
   * empty body — so `!error` was `true` and the store deleted the comment from
   * the screen for a delete Postgres had refused. The comment came back on the
   * next refetch, and nothing anywhere had said a word.
   *
   * NO OFFLINE QUEUE BRANCH here either, for the same reason as
   * {@link setAlbumCover}, and with the same obligation: a transport failure
   * says {@link NO_CONNECTION_MESSAGE}, never the permission wording.
   */
  async deleteComment(commentId: string): Promise<void> {
    const familyId = await getCurrentFamilyId();

    const REFUSED_MESSAGE = 'Коментарът не можа да бъде изтрит: нямате права.';

    const { data, error, status } = await this.client
      .from('photo_comments')
      .delete()
      .eq('family_id', familyId)
      .eq('id', commentId)
      .select('id');
    if (error && isTransientWriteFailure(status, error)) {
      throw new Error(NO_CONNECTION_MESSAGE, { cause: error });
    }
    if (error) {
      throw new Error(REFUSED_MESSAGE, { cause: error });
    }
    if (!data || data.length === 0) {
      const {
        data: survivor,
        error: confirmError,
        status: confirmStatus,
      } = await this.client
        .from('photo_comments')
        .select('id')
        .eq('family_id', familyId)
        .eq('id', commentId)
        .maybeSingle();
      const verdict = classifyZeroRowDelete(
        survivor !== null,
        confirmError,
        confirmStatus
      );
      // No queue to fall back on, so the transport class is surfaced rather
      // than swallowed — see the doc comment above.
      if (verdict === 'transient') {
        throw new Error(NO_CONNECTION_MESSAGE, { cause: confirmError });
      }
      if (verdict === 'refused') {
        throw new Error(REFUSED_MESSAGE);
      }
      // 'already-gone': the comment the user asked to remove is not there. That
      // is the outcome they wanted; saying so would only re-insert it via the
      // store's rollback.
    }
  }

  // =====================
  // Statistics
  // =====================

  async getAlbumStats(albumId: string): Promise<{
    photoCount: number;
    commentCount: number;
    memberCount: number;
  } | null> {
    const familyId = await getCurrentFamilyId();
    const { data: album } = await this.client
      .from('albums')
      .select('member_ids')
      .eq('family_id', familyId)
      .eq('id', albumId)
      .maybeSingle();
    if (!album) {
      return null;
    }

    const { data: photos } = await this.client
      .from('photos')
      .select('id')
      .eq('family_id', familyId)
      .eq('album_id', albumId);
    const photoIds = (photos ?? []).map((p) => p.id);

    let commentCount = 0;
    if (photoIds.length > 0) {
      const { count } = await this.client
        .from('photo_comments')
        .select('id', { count: 'exact', head: true })
        .eq('family_id', familyId)
        .in('photo_id', photoIds);
      commentCount = count ?? 0;
    }

    return {
      photoCount: photoIds.length,
      commentCount,
      memberCount: album.member_ids.length,
    };
  }

  async getOverallStats(): Promise<{
    totalAlbums: number;
    totalPhotos: number;
    totalComments: number;
    archivedAlbums: number;
  }> {
    const familyId = await getCurrentFamilyId();
    const [
      { count: activeAlbums },
      { count: archivedAlbums },
      { count: totalPhotos },
      { count: totalComments },
    ] = await Promise.all([
      this.client
        .from('albums')
        .select('id', { count: 'exact', head: true })
        .eq('family_id', familyId)
        .eq('is_archived', false),
      this.client
        .from('albums')
        .select('id', { count: 'exact', head: true })
        .eq('family_id', familyId)
        .eq('is_archived', true),
      this.client
        .from('photos')
        .select('id', { count: 'exact', head: true })
        .eq('family_id', familyId),
      this.client
        .from('photo_comments')
        .select('id', { count: 'exact', head: true })
        .eq('family_id', familyId),
    ]);

    return {
      totalAlbums: activeAlbums ?? 0,
      totalPhotos: totalPhotos ?? 0,
      totalComments: totalComments ?? 0,
      archivedAlbums: archivedAlbums ?? 0,
    };
  }

  // =====================
  // Utility
  // =====================

  reset(): void {
    // No-op for Supabase: persistent data is not reset client-side.
  }
}
